import clsx from 'clsx';
import React from 'react';

import type { FilesColumnDefinition } from '@gitbook/doc-core';
import type * as hiveCore from '@gitbook/hive-core';
import { createPalette, usePaletteHandle } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { AtomButton, LoadingIndicator } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { useContentReference } from '../../../ContentReferences';
import { FileIcon } from '../../../Files';
import { useInsertFiles } from '../../../InsertFilesDialog';
import { ContentEditorContext } from '../../contexts';
import { InsertValueButton } from './buttons';
import type { RecordValueRenderProps, TableDefinitionRenderSpec } from './types';

export const filesDefinitionSpec: TableDefinitionRenderSpec<string[], FilesColumnDefinition> = {
    title: 'Files',
    description:
        'Attachments allow you to add images, documents, or other files which can then be viewed or downloaded.',
    icon: Icon.Paperclip,
    ValueComponent: RecordFilesValue,
    onKeyDown: (key, value, setValue) => {
        switch (key) {
            case 'backspace':
                if (value.length > 0) {
                    setValue(value.slice(0, -1));
                }
                break;
        }
    },
};

/**
 * Cell content for a Files column.
 */
function RecordFilesValue(
    props: RecordValueRenderProps<string[], FilesColumnDefinition>
): React.ReactElement {
    const { value, selected, readOnly, displayMode, onValueChange, onKeyDown } = props;
    const {
        blocks: { withFiles },
    } = React.useContext(ContentEditorContext);

    const addButtonRef = React.useRef<HTMLButtonElement>(null);
    const insertFiles = useInsertFiles();

    // If the current cell is `selected`, focus the + button
    React.useEffect(() => {
        return selected ? addButtonRef.current?.focus() : addButtonRef.current?.blur();
    }, [selected]);

    /** User pressed to select files */
    const onPressAdd = withFiles
        ? async () => {
              const refs = await insertFiles.prompt({
                  title: 'Select file',
              });
              onValueChange([
                  ...value,
                  ...refs.filter((ref) => ref.kind === 'file').map((ref) => ref.file),
              ]);
              // Focus the + button after closing the files dialog
              addButtonRef.current?.focus();
          }
        : null;

    /** Remove a file from the listing */
    const onRemove = withFiles
        ? (index: number) => {
              const newValue = [...value];
              newValue.splice(index, 1);
              onValueChange(newValue);
          }
        : null;

    return (
        <div className="relative flex flex-1 flex-wrap gap-1">
            <React.Suspense fallback={<LoadingIndicator />}>
                {value.map((fileId, index) => (
                    <FileAttachmentItem
                        key={fileId}
                        fileId={fileId}
                        editable={
                            readOnly || !onRemove ? undefined : { onRemove: () => onRemove(index) }
                        }
                    />
                ))}
            </React.Suspense>
            {(displayMode === 'modal' || selected) && !readOnly && onPressAdd ? (
                <InsertValueButton
                    buttonRef={addButtonRef}
                    leadingIcon={value.length > 0 ? Icon.Pencil : Icon.Plus}
                    label="Add file"
                    tooltip={{
                        label: 'Add file',
                        shortcut: 'space',
                    }}
                    onKeyDown={onKeyDown}
                    onPress={onPressAdd}
                    active={insertFiles.isOpen()}
                />
            ) : null}
        </div>
    );
}

export function getFileGroupCommands(params: {
    fileId: string;
    editable: {
        onRemove: () => void;
    };
    file: hiveCore.RevisionFile | undefined;
    href: string | undefined;
}) {
    const { editable, file, href } = params;

    return {
        groups: [
            {
                key: 'file',
                commands: [
                    {
                        key: 'remove',
                        title: 'Remove',
                        icon: Icon.Trashcan,
                        trigger: () => {
                            editable.onRemove();
                        },
                    },
                    file
                        ? {
                              key: 'download',
                              title: 'Download',
                              icon: Icon.Download,
                              trigger: () => {
                                  window.open(href, '_blank');
                              },
                          }
                        : null,
                ].filter(filterOutNullable),
            },
        ],
    };
}

/** Item for a file in the table */
function FileAttachmentItem(props: {
    fileId: string;
    editable?: {
        onRemove: () => void;
    };
}): React.ReactElement {
    const { fileId, editable } = props;
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(buttonRef);
    const resolved = useContentReference({ kind: 'file', file: fileId });
    const file = resolved.resolution?.extra?.file;

    const onPress = () => {
        if (!editable) {
            if (resolved.href) {
                window.open(resolved.href, '_blank');
            }
        } else {
            paletteHandle.open(
                createPalette({
                    ...getFileGroupCommands({ fileId, editable, file, href: resolved.href }),
                })
            );
        }
    };

    return (
        <AtomButton
            ref={buttonRef}
            onClick={onPress}
            type="button"
            className={clsx('flex flex-grow', !editable && 'cursor-pointer hover:text-primary')}
        >
            <div className="flex flex-1 items-center gap-2">
                <FileIcon file={file} size={24} className="shrink-0" />
                <span className="truncate">{file?.name || 'Deleted'}</span>
            </div>
        </AtomButton>
    );
}
